import { ObjectId } from 'bson';
import type { Creator } from '@hero/schema';

/**
 * The synthetic identity stamped on `@Public()` HTTP requests that
 * arrive **without** a bearer token.
 *
 * Every inbound HTTP request lands in one of three identity tiers
 * (see `apps/backend/src/app/auth/README.md` → "Authentication
 * tiers"):
 *
 *   1. **Real user** — a JWT was presented and `TokenService.verifyToken`
 *      installed the resulting `AuthedUser` in CLS.
 *   2. **System employee** — an integration credential (Zapier /
 *      Mongodb / Moyasar) verified against a shared secret and
 *      installed the pre-baked `SUPER_ADMIN_ACCESS_TOKEN` identity.
 *      Docs it authors are attributed to the fixed hero-employee
 *      snapshot.
 *   3. **Anonymous** — `@Public()` + no token. `AuthGuard` installs
 *      the identity below so `DBCrud.newDoc` always has a valid
 *      `Creator` to stamp on writes (landing forms, prospect
 *      intake, future public "get a quote" flows, ...).
 *
 * Anonymous is deliberately **NOT a role**. `groups` / `roles` are
 * empty arrays, so every `@User(...)` / `@SuperAdmin()` gate rejects
 * an anonymous caller by construction. It only exists to give the
 * CRUD layer a well-formed `creator` — audit-trail hygiene, nothing
 * more.
 *
 * ### The fixed `ObjectId`
 *
 * Anonymous carries a real `ObjectId` — not a string sentinel — so
 * downstream consumers that materialise `creator.id` into an
 * `ObjectId` (audit-trail actor snapshots, Party materialisers,
 * tenant queries, timeline rows on public writes) keep working
 * without special-casing anonymous. Analogous to `superAdmin.heroId`.
 *
 * Trade-off: an authenticated code path that reads an
 * anonymous-authored doc's `creator.id` will silently accept it as a
 * "real" id. Callers that specifically need to distinguish anonymous
 * from real users MUST use {@link isAnonymousId} — never compare
 * against the literal hex.
 *
 * ### Frontend usage
 *
 * The admin-panel Leads / Feedback / etc. tables can render
 * anonymous-authored rows differently (e.g. show "Anonymous
 * submission" instead of the creator's name). Use
 * `CONFIG.anonymousUser.isAnonymousId(row.doc.creator.id)` — never
 * hard-code the literal hex at call sites.
 */

/**
 * The anonymous user id.
 *
 * **NOTE: THIS ID MUST NEVER CHANGE.**
 *
 * `682087f17063d777ffe4cb63`
 */
const ANONYMOUS_OBJECT_ID = new ObjectId('682087f17063d777ffe4cb63');
const ANONYMOUS_ID = ANONYMOUS_OBJECT_ID.toHexString();
const ANONYMOUS_NAME = 'Anonymous';

export const anonymousUser = {
  /**
   * ObjectId form — use for Mongo `_id`-shaped fields (Party `_id`,
   * `tenants[].id`, actor snapshots, etc.).
   *
   * **NOTE: THIS ID MUST NEVER CHANGE.**
   *
   * `682087f17063d777ffe4cb63`
   */
  _id: ANONYMOUS_OBJECT_ID,

  /**
   * Hex-string form — use for `creator.id` (schema is
   * `idSchema = z.coerce.string()`) and any other string-typed id
   * field. Equal to `_id.toHexString()`.
   */
  id: ANONYMOUS_ID,

  /** Display name stamped on `creator.name`. */
  name: ANONYMOUS_NAME,

  /**
   * Ready-to-stamp `Doc.creator` for anonymous public writes.
   *
   * Empty `groups` / `roles` mean no permission gate ever passes
   * for an anonymous caller — `isUser('customer' | 'super_admin' |
   * ...)` returns `false` for every main type.
   */
  creator: {
    id: ANONYMOUS_ID,
    groups: [],
    roles: [],
    name: ANONYMOUS_NAME,
  } satisfies Creator,

  /**
   * True when the given id refers to the anonymous sentinel. Accepts
   * hex strings, `ObjectId` instances, or anything with a
   * `.toString()` that yields the canonical hex — so callers can
   * pass raw `creator.id`, a Party `_id`, or a Mongo query result
   * unchanged.
   */
  isAnonymousId: (id: unknown): boolean =>
    id != null && String(id) === ANONYMOUS_ID,
} as const;
